import { useEffect, useState } from "react"
import InfoBox from "../ui/InfoBox"
import SectionHeader from "../ui/SectionHeader"
import { FiPaperclip, FiCheckCircle } from "react-icons/fi"
import { validatePatentPdfFile } from "../../utils/fileUploadUtils"
import { formatApiError } from "../../utils/formatApiError"
import FileUploadSlot from "./FileUploadSlot"
import {
  uploadPatentDocument,
  PatentDocumentType
} from "../../services/patentDocumentService"
import { useResetListener } from "../../hooks/useResetListener"

type PatentDocument = {
  type: PatentDocumentType
  fileName: string
  epoDocumentId?: string
}

const REQUIRED_SLOTS: { type: PatentDocumentType; label: string }[] = [
  { type: "DESCRIPTION", label: "Description" },
  { type: "CLAIMS", label: "Claims" },
  { type: "ABSTRACT", label: "Abstract" }
]

const OPTIONAL_SLOTS: { type: PatentDocumentType; label: string }[] = [
  { type: "DRAWINGS", label: "Drawings" }
]

export default function DocumentsSection({
  onChange,
  initialData,
  claimsFiledLater = false,
  onClaimsFiledLaterChange,
  translationFiledLater = false,
  onTranslationFiledLaterChange,
  numberOfClaims = null,
  onNumberOfClaimsChange,
  disabled = false
}: {
  onChange?: (list: PatentDocument[]) => void
  initialData?: PatentDocument[] | null
  claimsFiledLater?: boolean
  onClaimsFiledLaterChange?: (value: boolean) => void
  translationFiledLater?: boolean
  onTranslationFiledLaterChange?: (value: boolean) => void
  numberOfClaims?: number | null
  onNumberOfClaimsChange?: (value: number | null) => void
  disabled?: boolean
}) {
  const [documents, setDocuments] = useState<PatentDocument[]>(initialData || [])
  const [uploadingType, setUploadingType] = useState<PatentDocumentType | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (initialData) {
      setDocuments(initialData)
    }
  }, [initialData])

  useResetListener(() => {
    setDocuments([])
    setErrors({})
    onChange?.([])
    onClaimsFiledLaterChange?.(false)
    onTranslationFiledLaterChange?.(false)
    onNumberOfClaimsChange?.(null)
  })

  const handleSelect = async (type: PatentDocumentType, file: File) => {
    const validationError = validatePatentPdfFile(file)
    if (validationError) {
      setErrors((prev) => ({ ...prev, [type]: validationError }))
      return
    }

    setErrors((prev) => ({ ...prev, [type]: "" }))
    setUploadingType(type)

    try {
      const result = await uploadPatentDocument(file, type)

      setDocuments((prev) => {
        const updated = [
          ...prev.filter((doc) => doc.type !== type),
          { type, fileName: result.fileName, epoDocumentId: result.documentId }
        ]
        onChange?.(updated)
        return updated
      })
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        [type]: formatApiError(error, "Upload failed. Please try again.")
      }))
    } finally {
      setUploadingType(null)
    }
  }

  const handleDelete = (type: PatentDocumentType) => {
    if (disabled) return

    setDocuments((prev) => {
      const updated = prev.filter((doc) => doc.type !== type)
      onChange?.(updated)
      return updated
    })
  }

  const requiredSlots = claimsFiledLater
    ? REQUIRED_SLOTS.filter((slot) => slot.type !== "CLAIMS")
    : REQUIRED_SLOTS
  const requiredUploadedCount = requiredSlots.filter((slot) =>
    documents.some((doc) => doc.type === slot.type)
  ).length
  const allRequiredUploaded = requiredUploadedCount === requiredSlots.length

  return (
    <div>
      <SectionHeader icon={<FiPaperclip size={20} />} title="Documents">
        <InfoBox title="Required documents">
          Upload the description, claims and abstract of your invention as
          PDF files (Art. 78 EPC). Drawings are optional; add them if your
          invention includes any.
        </InfoBox>
      </SectionHeader>

      <div
        className={`mb-4 flex items-center gap-2 text-sm font-medium ${
          allRequiredUploaded ? "text-green-700" : "text-gray-500"
        }`}
      >
        <FiCheckCircle size={16} className={allRequiredUploaded ? "text-green-600" : "text-gray-300"} />
        {requiredUploadedCount} / {requiredSlots.length} required documents uploaded
      </div>

      <div className="flex flex-col gap-4">
        {REQUIRED_SLOTS.map((slot) => {
          const optional = claimsFiledLater && slot.type === "CLAIMS"
          return (
            <FileUploadSlot
              key={slot.type}
              label={optional ? slot.label : `${slot.label} *`}
              fileName={documents.find((d) => d.type === slot.type)?.fileName}
              documentId={documents.find((d) => d.type === slot.type)?.epoDocumentId}
              uploading={uploadingType === slot.type}
              error={errors[slot.type] || ""}
              disabled={disabled}
              onSelect={(file) => handleSelect(slot.type, file)}
              onDelete={() => handleDelete(slot.type)}
            />
          )
        })}
      </div>

      {!claimsFiledLater && (
        <div className="mt-4 flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700" htmlFor="number-of-claims">
            Number of claims *
          </label>
          <input
            id="number-of-claims"
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            value={numberOfClaims ?? ""}
            disabled={disabled}
            onChange={(e) => {
              const parsed = parseInt(e.target.value, 10)
              onNumberOfClaimsChange?.(Number.isNaN(parsed) || parsed < 1 ? null : parsed)
            }}
            className="w-32 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-dantzBlue focus:ring-1 focus:ring-dantzBlue disabled:bg-gray-50"
          />
          <span className="text-xs text-gray-500">
            The total number of claims in your claims document. The EPO uses it to calculate the claims fee (the first 15 claims are free).
          </span>
        </div>
      )}

      <div className="mt-4 flex flex-col gap-2">
        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={claimsFiledLater}
            disabled={disabled}
            onChange={(e) => onClaimsFiledLaterChange?.(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-dantzBlue focus:ring-dantzBlue"
          />
          Claims will be filed later (Rule 57(c) / 58 EPC)
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={translationFiledLater}
            disabled={disabled}
            onChange={(e) => onTranslationFiledLaterChange?.(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-dantzBlue focus:ring-dantzBlue"
          />
          A translation of the application will be filed later
        </label>
      </div>

      <div className="mb-1 mt-6 text-xs font-semibold uppercase tracking-wide text-gray-400">
        Optional
      </div>

      <div className="flex flex-col gap-4">
        {OPTIONAL_SLOTS.map((slot) => (
          <FileUploadSlot
            key={slot.type}
            label={slot.label}
            fileName={documents.find((d) => d.type === slot.type)?.fileName}
            documentId={documents.find((d) => d.type === slot.type)?.epoDocumentId}
            uploading={uploadingType === slot.type}
            error={errors[slot.type] || ""}
            disabled={disabled}
            onSelect={(file) => handleSelect(slot.type, file)}
            onDelete={() => handleDelete(slot.type)}
          />
        ))}
      </div>
    </div>
  )
}
